import { createFlow, Flow } from '@adv-frontend/modules-flow';
import { memoize } from '@adv-frontend/modules-memo';
import { shallowEqual } from 'react-redux';
import type { AnySelector, DropFirstParameter, GetStoreFn } from '../types';
import { createListenersCollection } from '../createListenersCollection';
import { BoundSelectorAndHook, getBoundSelectorAndHook } from './getBoundSelectorAndHook';
import { triggerSelectorCall } from './trackSelectorCall';
import { subscribeToSelector } from './subscribeToSelector';
import { addCurrentSelectorStore } from './createSubscribableSelector';

export interface CreateSelectorFlowOptions<Selector extends AnySelector> {
    /**
     * Функция для получения селектора
     * Передаем селектор как функцию для того,
     * чтобы была возможность его подменить после инициализации приложения
     */
    getSelector: () => Selector;
    /**
     * Функция для получения стора
     */
    getStore: GetStoreFn;
}

export type SelectorFlowFactory<Selector extends AnySelector> = ((
    ...selectorArgs: Parameters<DropFirstParameter<Selector>>
) => Flow<ReturnType<Selector>>) &
    BoundSelectorAndHook<Selector>;

export function createSelectorFlowFactory<Selector extends AnySelector>(
    options: CreateSelectorFlowOptions<Selector>
): SelectorFlowFactory<Selector> {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Добавялем типы для tuple
    const factory = getSelectorFlowFactory(options) as unknown as SelectorFlowFactory<Selector>;
    const [hook, getter] = getBoundSelectorAndHook<Selector>(options);

    Object.defineProperty(factory, Symbol.iterator, {
        value: function* () {
            yield hook;
            yield getter;
        },
        enumerable: false,
    });

    factory[0] = hook;
    factory[1] = getter;

    return factory;
}

function getSelectorFlowFactory<Selector extends AnySelector>({
    getStore,
    getSelector,
}: CreateSelectorFlowOptions<Selector>) {
    type SelectorResult = ReturnType<Selector>;
    type SelectorArgsWithoutState = Parameters<DropFirstParameter<Selector>>;

    const flowFactory = memoize(
        (selectorArgs: SelectorArgsWithoutState) => {
            return createSelectorFlow({
                getStore,
                getSelector,
                selectorArgs,
            });
        },
        {
            equals(a, b) {
                return shallowEqual(a, b);
            },
        }
    );

    return (...selectorArgs: SelectorArgsWithoutState): Flow<SelectorResult> => {
        return flowFactory(selectorArgs);
    };
}

function createSelectorFlow<Selector extends AnySelector>({
    getStore,
    getSelector,
    selectorArgs,
}: CreateSelectorFlowOptions<Selector> & {
    selectorArgs: Parameters<DropFirstParameter<Selector>>;
}): Flow<ReturnType<Selector>> {
    type SelectorState = Parameters<Selector>[0];
    type SelectorResult = ReturnType<Selector>;

    const getState = () => {
        const store = getStore();
        const selector = getSelector();

        /**
         * Сообщает о вызове модульного селектора, для проверки использования во внешних сторах
         */
        triggerSelectorCall();

        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Стор типизирован через any
        return selector(store.getState() as SelectorState, ...selectorArgs) as SelectorResult;
    };

    /** Итог чтения селектора: прочитанное значение либо ошибка, которой чтение закончилось */
    type Outcome = { ok: true; value: SelectorResult } | { ok: false; error: unknown };

    // Сравниваем через Object.is, как и потоки вычислений (hasSameOutcome в modules-flow):
    // === считал бы два NaN разными итогами и уведомлял подписчиков на каждом чтении,
    // а -0 и 0 — одинаковыми, и смена знака нуля не доехала бы до потока
    const isSameOutcome = (a: Outcome, b: Outcome) => {
        if (a.ok && b.ok) {
            return Object.is(a.value, b.value);
        }

        return !a.ok && !b.ok && Object.is(a.error, b.error);
    };

    // Итог последнего чтения хранится в самом потоке: и публичные геттеры, и уведомления
    // подписчиков идут от одного состояния. Держи мы его отдельно, чтение без подписчиков
    // меняло бы кеш, не трогая поток, и последующая синхронизация на подписке возвращала бы
    // прежний итог потока молча — пассивный потребитель пропустил бы смену значения
    const flow = createFlow<Outcome>({ ok: true, value: getState() });

    /** Записывает итог в поток, не уведомляя подписчиков повторно тем же итогом */
    const setOutcome = (outcome: Outcome) => {
        if (isSameOutcome(flow.getSnapshot(), outcome)) {
            return;
        }

        flow.emit(outcome);
    };

    const unwrap = (outcome: Outcome) => {
        if (!outcome.ok) {
            throw outcome.error;
        }

        return outcome.value;
    };

    const getOutcome = (): Outcome => {
        let outcome: Outcome;

        try {
            outcome = { ok: true, value: getState() };
        } catch (error) {
            outcome = { ok: false, error };
        }

        const previousOutcome = flow.getSnapshot();

        return isSameOutcome(previousOutcome, outcome) ? previousOutcome : outcome;
    };

    const listeners = createListenersCollection({
        onAdd(listener) {
            return flow.subscribe(listener);
        },
        onActivate() {
            return subscribeToSelector(
                () => {
                    const store = getStore();
                    addCurrentSelectorStore(store);
                    return getOutcome();
                },
                outcome => {
                    try {
                        setOutcome(outcome);
                    } catch (err) {
                        // Метод emit() проксирует ошибки от слушателей потока.
                        // Не хотим, чтобы ошибки слушателей потока влияли на работу самого потока,
                        // поэтому ловим ошибки и просто их логируем
                        console.error(err);
                    }
                }
            );
        },
    });

    return {
        subscribe(listener) {
            return listeners.add(listener);
        },
        getSnapshot() {
            if (!listeners.hasListeners()) {
                try {
                    const value = getState();
                    setOutcome({ ok: true, value });
                    return value;
                } catch (err) {
                    setOutcome({ ok: false, error: err });
                    throw err;
                }
            }

            return unwrap(flow.getSnapshot());
        },
        getCachedSnapshot() {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- поток создан через createFlow с итогом первого чтения, кеш непустой с момента создания
            return unwrap(flow.getCachedSnapshot()!);
        },
    };
}
